import { EllipsisMenu, NodeStatus } from 'components';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import dayjs from 'dayjs';
import { groupSearchFilter } from 'features/integrations/utils/integrationSearchUtils';
import { useCallback, useMemo, useState } from 'react';
import { FaCog } from 'react-icons/fa';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { getDeviceListForGroup } from 'utils/iconUtils';
import { DeviceRow } from './DeviceRows';

export default function Table({
  hasCreateEditPermission,
  hasDeletePermission,
  onEditGroup,
  onDeleteGroup,
  onOpenGroupSettings,
}) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const { data: deviceGroups = [], isFetching: deviceGroupsFetching } = useGetGroupsQuery();
  const [ellipsisOpenGroupId, setEllipsisOpenGroupId] = useState(null);
  const cellWrapperClass = 'd-flex align-items-center gap-2 flex-wrap';

  //TODO: Will be converted into its own feature when making dual status for device groups
  const getDevices = useCallback(() => connectedDevices, [connectedDevices]);

  const getGroupStatus = (group) => {
    // Merge all device IDs from devices, controllers, and auxDevices
    const allDeviceIds = [
      ...(group.devices || []),
      ...(group.controllers || []),
      ...(group.auxDevices || []),
    ];
    const uniqueDeviceIds = Array.from(new Set(allDeviceIds));

    const devices = getDevices().filter((device) => uniqueDeviceIds.includes(device.id));
    // If no devices found, return null for empty group
    if (devices.length === 0) {
      return null;
    }

    // Filter devices that have the expected status data structure
    const statusDevices = devices.filter(
      (device) =>
        device &&
        device.data &&
        Array.isArray(device.data) &&
        device.data.length > 0 &&
        device.data[0] &&
        typeof device.data[0].status !== 'undefined',
    );

    // If no devices with status data, return the first device (fallback)
    if (statusDevices.length === 0) {
      return devices.length > 0 ? devices[0] : null;
    }

    const hasAlertingDevice = statusDevices.some((device) => device.data[0].status);
    const hasOfflineDevice = statusDevices.some((device) => device.data[0].is_offline);
    const hasMaintenanceDevice = statusDevices.some(
      (device) => device.kill_switch || device.data[0].status === -1,
    );

    let useThisDevice = statusDevices.find((device) => {
      if (hasMaintenanceDevice) {
        return device.kill_switch || device.data[0].status === -1;
      }
      if (hasAlertingDevice) {
        return device.data[0].status === 1;
      }
      if (hasOfflineDevice) {
        return device.is_offline;
      }
      return device.data[0].status === 1;
    });

    if (!useThisDevice) {
      useThisDevice = statusDevices.find((device) => device.data[0].status === 0);
    }

    if (!useThisDevice) {
      useThisDevice = statusDevices[0];
    }
    return useThisDevice;
  };

  const rows = useMemo(() => {
    return (
      deviceGroups
        ?.filter((group) => groupSearchFilter(group, connectedDevices))
        ?.map((group) => ({
          ...group,
          id: group.id,
          groupStatus: getGroupStatus(group),
          devicesCount: group.devices?.length ?? 0,
          dateCreatedFormatted: dayjs(group?.date_created).format('MM/DD/YYYY, hh:mm:ss A'),
        })) ?? []
    );
  }, [deviceGroups, connectedDevices]);

  // Columns drive the BaseTable plus detail panel dropdown per group.
  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        Cell: ({ cell }) => (
          <div className={cellWrapperClass}>
            <span className="text-break">{cell.getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ row }) => {
          const group = row.original;
          return (
            <div className={`${cellWrapperClass} w-100`}>
              <img
                className="d-none d-md-block"
                src={getDeviceListForGroup(group)}
                alt={group?.type || 'RescAlert'}
                width={'30px'}
              />
              <p className="m-0 text-truncate text-break">{group?.name}</p>
            </div>
          );
        },
      },
      {
        accessorKey: 'devicesCount',
        header: 'Device(s)',
        Cell: ({ cell }) => (
          <div className={cellWrapperClass}>
            <span>{cell.getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'dateCreatedFormatted',
        header: 'Date Created',
        Cell: ({ cell }) => (
          <div className={cellWrapperClass}>
            <span className="text-break">{cell.getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'groupStatus',
        header: 'STATUS',
        Cell: ({ cell }) => (
          <div className={cellWrapperClass}>
            <NodeStatus className="text-break" device={cell.getValue()} />
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 72,
        minSize: 60,
        maxSize: 120,
        grow: false,
        Cell: ({ row }) => {
          const group = row.original;

          return (
            <div className="d-flex align-items-center justify-content-end gap-3 w-100 pe-1">
              {hasCreateEditPermission && (
                <button
                  type="button"
                  className="btn btn-link p-0 text-white"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenGroupSettings?.(group?.id);
                  }}
                >
                  <FaCog size={24} />
                </button>
              )}
              {(hasCreateEditPermission || hasDeletePermission) && (
                <EllipsisMenu
                  containerClassname="bg-primary p-0"
                  open={ellipsisOpenGroupId === group?.id}
                  onOpen={() => setEllipsisOpenGroupId(group?.id)}
                  onClose={() => setEllipsisOpenGroupId(null)}
                >
                  {hasCreateEditPermission && (
                    <button
                      className="btn btn-primary w-100 text-center"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEllipsisOpenGroupId(null);
                        onEditGroup(group?.id);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {hasDeletePermission && (
                    <button
                      className="btn btn-danger w-100 text-center"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEllipsisOpenGroupId(null);
                        onDeleteGroup(group);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </EllipsisMenu>
              )}
              {row.getIsExpanded() ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
            </div>
          );
        },
      },
    ],
    [
      hasCreateEditPermission,
      hasDeletePermission,
      onDeleteGroup,
      onEditGroup,
      onOpenGroupSettings,
      ellipsisOpenGroupId,
    ],
  );

  const dropdownMenu = (group) => {
    if (connectedDevicesLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-3 text-secondary">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          Loading devices...
        </div>
      );
    }

    if (connectedDevicesError) {
      return <div className="p-2 text-center text-danger">Unable to load devices.</div>;
    }

    if (!group?.devices?.length)
      return <div className="p-2 text-center text-muted">No devices in this group.</div>;

    return (
      <div className="w-100 px-5">
        <div className="row px-2 py-1 border-bottom border-secondary fw-bold">
          <div className="col-12 col-md-4 col-lg-3">Device</div>
          <div className="col-12 col-md-3 col-lg-2">Available Licenses</div>
          <div className="col-12 col-md-2 col-lg-2">Status</div>
          <div className="col-12 col-md-2 col-lg-3">Date Created</div>
          <div className="col-12 col-md-1 col-lg-2" />
        </div>
        {group.devices.map((deviceId, idx) => (
          <DeviceRow
            key={`${group.id}-${deviceId}-${idx}`}
            deviceId={deviceId}
            last={idx === group.devices.length - 1}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="mt-3 d-none d-lg-block">
      {!deviceGroupsFetching ? (
        <BaseTable
          columns={columns}
          rows={rows}
          expandable
          altRowSettings={{
            alternate: true,
            color1: '#222A2D',
            color2: '#2C373B',
          }}
          // conditionally disable row virtualization for better dropdown performance
          enableRowVirtualization={rows.length > 50}
          initialState={{
            columnVisibility: {
              licenses: false,
              status: false,
            },
          }}
          customStyles={{
            cell: {
              color: '#ffffff',
              borderBottom: '1px solid #56577A',
              cursor: 'pointer',
              padding: '12px 16px',
            },
            header: { padding: '12px 16px' },
          }}
          dropdownMenu={dropdownMenu}
        />
      ) : (
        <WidgetLoadingAnimation />
      )}
    </div>
  );
}
